import { useCallback, useEffect, useRef, useState } from 'react';
import type { WorkshopCard } from '../data/lacEpauleSeptembre2026Data';
export function isCompanyResultsPayload(value: unknown): value is WorkshopCard {
  if (!value || typeof value !== 'object') return false;
  const card = value as WorkshopCard, data = card.companyResults;
  return card.id === 'resultats-entreprise-bz' && !!data && Array.isArray(data.years) && data.years.length > 0 && data.years.every(y => Number.isInteger(y.year) && ['revenue', 'profit', 'value', 'dividends', 'margin'].every(k => Number.isFinite(y[k as keyof typeof y]))) && !!data.reconciliation && Array.isArray(data.ytd);
}
// No localStorage, query cache, module-global payload or stale-response fallback.
export function useCompanyResults(identity?: string) {
  const [state, setState] = useState<{ identity: string; payload: WorkshopCard } | null>(null);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const request = useRef<AbortController>();
  const clear = useCallback(() => { generation.current++; request.current?.abort(); setState(null); }, []);
  const refresh = useCallback(async () => {
    clear(); setError('');
    if (!identity || window.document.hidden) return;
    const current = generation.current;
    const controller = new AbortController(); request.current = controller;
    try {
      const response = await fetch('/api/ce/company-results', { credentials: 'include', cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Accès privé indisponible ou refusé. Aucune donnée conservée à l’écran.');
      const payload: unknown = await response.json();
      if (!isCompanyResultsPayload(payload)) throw new Error('Réponse privée invalide.');
      if (current === generation.current && !window.document.hidden) setState({ identity, payload });
    } catch (failure) {
      if (current === generation.current && !controller.signal.aborted) { setState(null); setError(failure instanceof Error ? failure.message : 'Consultation indisponible.'); }
    }
  }, [identity, clear]);
  useEffect(() => {
    void refresh();
    const visible = () => { if (window.document.hidden) clear(); else void refresh(); };
    const focus = () => { void refresh(); };
    window.document.addEventListener('visibilitychange', visible);
    window.addEventListener('focus', focus); window.addEventListener('pageshow', focus);
    window.addEventListener('pagehide', clear);
    const timer = window.setInterval(focus, 60_000);
    return () => { clear(); window.clearInterval(timer); window.document.removeEventListener('visibilitychange', visible); window.removeEventListener('focus', focus); window.removeEventListener('pageshow', focus); window.removeEventListener('pagehide', clear); };
  }, [refresh, clear]);
  return { payload: state && state.identity === identity ? state.payload : null, error, refresh };
}
